/* eslint-disable no-shadow */
/* eslint-disable no-unused-vars */
import React, { useState, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import Table from "../../../components/Table/Table";
import { Header } from "../../../components";
import { PendingSRNBaseColumns } from "../../../utils/utils";
import TicketButton from "../../../components/TableComponents/TicketButton/TicketButton";
import TableLayout from "../../../layouts/TableLayout/TableLayout";
import useTicketFilters from "../../../utils/ticketFilters";
import useTicketData from "../../../hooks/useTicketData";
import ActionButtons from "../../../components/TableComponents/ActionButtons/ActionButtons";
import AddSRNDialogBox from "../../../components/AddSRNDialogBox/AddSRNDialogBox";
import useTicketExport from "../../../hooks/useTicketExport";
import NoItemsFound from "../../../components/NoItemsFound/NoItemsFound";
import { API } from "../../../apis/api";
import {
  errorSnackbar,
  successSnackbar,
} from "../../../redux/features/customSnackbar/action";

export const initialRows = [
  {
    ticketNumber: "TKT001234",
    folio: "FOLIO12345",
    srn: "SRN789456",
    updatedOn: "2023-10-01",
    updatedBy: "User1",
  },
  {
    ticketNumber: "TKT001235",
    folio: "FOLIO67890",
    srn: "SRN123789",
    updatedOn: "2023-10-02",
    updatedBy: "User2",
  },
  {
    ticketNumber: "TKT001236",
    folio: "FOLIO54321",
    srn: "SRN654987",
    updatedOn: "2023-10-03",
    updatedBy: "User3",
  },
  {
    ticketNumber: "TKT001237",
    folio: "FOLIO98765",
    srn: "SRN321654",
    updatedOn: "2023-10-04",
    updatedBy: "User4",
  },
  {
    ticketNumber: "TKT001238",
    folio: "FOLIO13579",
    srn: "SRN987321",
    updatedOn: "2023-10-05",
    updatedBy: "User5",
  },
  {
    ticketNumber: "TKT001239",
    folio: "FOLIO24680",
    srn: "SRN135790",
    updatedOn: "2023-10-06",
    updatedBy: "User6",
  },
  {
    ticketNumber: "TKT001240",
    folio: "FOLIO98765",
    srn: "SRN456123",
    updatedOn: "2023-10-07",
    updatedBy: "User7",
  },
  {
    ticketNumber: "TKT001241",
    folio: "FOLIO55555",
    srn: "SRN789012",
    updatedOn: "2023-10-08",
    updatedBy: "User8",
  },
];

function PendingSRN() {
  const dispatch = useDispatch();
  const userId = useSelector((state)=>state.auth.user.userId)
  const [openDialogBox, setOpenDialogBox] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [formData, setFormData] = useState({
    files: [],
    srn: "",
    date: null,
  });
  const [selectedRow, setSelectedRow] = useState(null);
  const {
    searchQuery,
    allRows: filteredRows,
    setAllRows,
    loading,
    isRefreshing,
    handleRefresh,
    handleSearchQuery,
    total,
    page,
    rowsPerPage,
    handleChangePage,
    handleChangeRowsPerPage,
  } = useTicketData("EL_GENERATED", true);

  const handleOpenDialog = () => {
    setOpenDialogBox(true);
  };
  const handleCloseDialog = () => {
    setOpenDialogBox(false);
  };

  const handleActionClick = useCallback((action, rowData) => {
    if (action === "Add SRN") {
      setSelectedRow(rowData);
      console.log("Selected Row:", rowData);
      setFormData({ files: [], srn: "", date: null });
      handleOpenDialog();
    }
  }, []);

  const uploadSRNToAPI = async (ticketCode, data) => {
    const form = new FormData();
    form.append("srn", data.srn);
    if (data.date) form.append("srn_update_datetime", data.date.toISOString());
    data.files.forEach((file) => form.append("file", file));
    form.append("document_type", "srn_document");
    form.append("ticket_code", ticketCode);
    form.append("sub",userId);
    const response = await API.post("/document/upload-document/", form, {
      headers: {
        "Content-Type": "multiipart/form-data",
      },
    });
    return response.data;
  };

  const handleAddSRN = useCallback(
    async (data) => {
      if (data.srn && selectedRow) {
        console.log("selectedRow..",selectedRow)
        try {
          setIsUploading(true);
          await uploadSRNToAPI(selectedRow.ticketNumber, data);
          const updatedRows = filteredRows.map((row) =>
            row.id === selectedRow.id ? { ...row, srn: data.srn } : row
          );
          dispatch(
            successSnackbar({
              title: "Add SRN successfully",
              subtitle: `Add SRN successfully of ticket no. : ${selectedRow.ticketNumber}`,
              duration: 5000,
            })
          );
          setAllRows(updatedRows);
          setFormData({ files: [], srn: "", date: null });
          handleRefresh();
        } catch (error) {
          console.error("Failed to upload SRN info:", error);
          dispatch(
            errorSnackbar({
              title: "Add SRN failed",
              subtitle: `Add SRN failed of ticket no. : ${selectedRow.ticketNumber}`,
              duration: 5000,
            })
          );
          setIsUploading(false);
        }
      }
    },
    [selectedRow, filteredRows, setAllRows, handleRefresh]
  );

  const columns = useMemo(() => {
    const updatedColumns = [...PendingSRNBaseColumns];

    updatedColumns[0] = {
      width: 180,
      title: "Ticket Number",
      field: "ticketNumber",
      enableSearch: false,
      enableFilter: false,
      render: (rowData) => (
        <TicketButton
          ticketNumber={rowData.ticketNumber}
          ticketId={rowData.id}
        />
      ),
    };

    updatedColumns[3] = {
      width: 150,
      title: "Updated On",
      field: "updatedOn",
      enableSearch: false,
      enableFilter: false,
      render: (rowData) => {
        if (!rowData.updatedOn) return null;

        const date = new Date(rowData.updatedOn);
        const formattedDate = date.toLocaleString("en-US", {
          month: "2-digit",
          day: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        });

        return (
          <div style={{ color: "#1F1F1F" }}>
            {formattedDate.replace(",", "")}
          </div>
        );
      },
    };

    updatedColumns[5] = {
      width: 220,
      title: "Action",
      field: "action",
      enableSearch: false,
      enableFilter: false,
      render: (rowData) => (
        <ActionButtons
          actions={["Add SRN"]}
          onActionClick={(action) => handleActionClick(action, rowData)}
        />
      ),
    };

    return updatedColumns;
  }, []);

  const { handleExport, isExporting } = useTicketExport(
    "EL_GENERATED",
    columns
  );

  return (
    <TableLayout
      styles={{ tableInnerWrapper: { paddingBottom: "0rem" } }}
      header={
        <Header
          searchQuery={searchQuery}
          handleSearchQuery={handleSearchQuery}
          onRefresh={handleRefresh}
          isRefreshing={isRefreshing}
          onExport={handleExport}
          isExporting={isExporting}
          disableExport={filteredRows.length === 0}
        />
      }
    >
      {filteredRows.length === 0 && !(loading) ? (
        <NoItemsFound />
      ) : (
        <Table
          columns={columns}
          defaultColumns={columns}
          rows={filteredRows}
          hideCheckbox
          rowHeight={45}
          setHasActiveEdits={() => {}}
          isDataLoading={loading || isRefreshing}
          serverSide
          totalRows={total}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      )}
      <AddSRNDialogBox
        openDialogBox={openDialogBox}
        onClose={handleCloseDialog}
        setFormData={setFormData}
        OnSave={handleAddSRN}
        isUploading={isUploading}
        formData={formData}
      />
    </TableLayout>
  );
}

PendingSRN.propTypes = {};

export default PendingSRN;
